<script setup lang="ts">
import { formValidation } from '@/utils/formValidation'

const props = defineProps<{
    form: HTMLFormElement | null
    customerComment?: string
    newsletter?: boolean
}>()

const emit = defineEmits(['update:selectStep', 'onError', 'orderPlaced'])

const { validateForm } = formValidation()
const { refreshCart } = useCart()
const { createOrder } = useCheckout()
const { handlePayment } = usePayment()
const { showNotification } = useNotification()
const { t } = useI18n()
const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)
const { user } = useUser()
const { getSalutationId, subscribeToNewsletter } = useNewsletterSubscription()
const showOverlay = useState<boolean>('checkout-overlay', () => false)

const isPlacingOrder = ref(false)

async function handlePlaceOrder() {
    if (!props.form) return
    const isValid = await validateForm(props.form)
    if (!isValid) return

    isPlacingOrder.value = true
    showOverlay.value = true

    try {
        const order = await createOrder({ customerComment: props.customerComment })

        if (!order?.id) throw new Error('Order creation failed')

        const paymentResult = await handlePayment(order.id)

        // Subscribe to newsletter if checkbox was checked
        if (props.newsletter && user.value) {
            try {
                const salutationId = getSalutationId(user.value)
                if (salutationId) {
                    await subscribeToNewsletter(
                        user.value.email,
                        salutationId,
                        user.value.firstName,
                        user.value.lastName
                    )
                }
            } catch (error) {
                // Don't fail the order if newsletter subscription fails
                console.error('Newsletter subscription failed:', error)
            }
        }

        if (paymentResult.redirectUrl) {
            emit('orderPlaced', order.id)
            window.location.href = paymentResult.redirectUrl
        } else {
            await navigateTo(formatLink(`/checkout/success?orderId=${order.id}`))
            await refreshCart()
        }
    } catch (err) {
        showOverlay.value = false
        const message = err instanceof Error ? err.message : t('checkout.summary.orderError')
        showNotification(message, 'error', true)

        if (message.includes('payment-method-blocked')) {
            emit('onError', 'payment-method-blocked')
        }
    } finally {
        isPlacingOrder.value = false
    }
}

function selectStep(step: string) {
    emit('update:selectStep', step)
}
</script>

<template>
    <Teleport to="#checkoutNavigation">
        <nav class="navigation flex flex-col flex-wrap justify-between items-center gap-2 lg:flex-row lg:flex-nowrap lg:items-center">
            <div
                class="btn btn-lg btn-tertiary w-full order-2 lg:order-1 lg:w-auto lg:btn-sm"
                role="button"
                tabindex="0"
                @click="selectStep('payment')"
                @keyup.enter="selectStep('payment')"
            >
                <MiscIcon name="chevron-left" />
                {{ $t('checkout.summary.navigationBack') }}
            </div>
            <div class="w-full flex flex-col items-end gap-2 order-1 lg:w-auto lg:order-2">
                <button
                    type="submit"
                    :disabled="isPlacingOrder"
                    class="btn btn-lg btn-accent w-full lg:w-auto"
                    data-cy="checkout-navigation-place-order"
                    @click.prevent="handlePlaceOrder()"
                >
                    <span v-if="isPlacingOrder" class="loading" />
                    <MiscIcon v-else name="check" />
                    {{ $t('checkout.summary.navigationPlaceOrder') }}
                </button>
                <div class="gap-1 items-center mt-3 text-base-content text-xs font-normal flex lg:hidden">
                    <MiscIcon name="lock" size="xs" />
                    {{ $t('cart.transaction') }}
                    <div class="font-bold underline lowercase">
                        {{ $t('cart.safe') }}
                    </div>
                </div>
            </div>
        </nav>
    </Teleport>
</template>
